import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { format, parseISO } from 'date-fns';

import MainTitle from '../common/MainTitle';
import Modal from '../common/Modal';
import Helper from '../common/Helper';
import Toasts from '../common/Toasts';

import './BackupInstructions.css';

import BackupInstructionsService from './BackupInstructionsService';
import BackupInstructionEditor from './BackupInstructionEditor';


const BackupInstructions = () => {

    const [message, setMessage] = useState('');
    const [showNewModal, setShowNewModal] = useState(false);

    const [cloneData, setCloneData] = useState();

    const [justCopyBackupInstructionKey, setJustCopyBackupInstructionKey] = useState(null);

    const navigate = useNavigate();
    const { backupInstructionKey } = useParams();

    const [backupInstructions, setBackupInstructions] = useState("");

    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(Helper.defaults.PAGE_SIZE);
    const [showMore, setShowMore] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const isFirstSearchRender = useRef(true);

    const getBackupInstructions = () => {
        const search = (searchQuery || '').trim() || null;
        BackupInstructionsService.get(1, currentPage * pageSize, search).then(
            (response) => {
                setShowMore(response.length == pageSize);
                const all = [];
                all.push(...response);
                if (backupInstructionKey && !response.find(x => x.key == backupInstructionKey)) {
                    BackupInstructionsService.getOne(backupInstructionKey).then((backupInstructionResponse) => {
                        all.push(backupInstructionResponse);
                        setBackupInstructions(all);
                    });
                } else {
                    setBackupInstructions(all);
                }
            },
            (error) => {
                setMessage(Helper.errorToString(error));
            }
        );
    }

    const loadMore = () => {
        const page = currentPage ? currentPage + 1 : 1;
        const search = (searchQuery || '').trim() || null;
        BackupInstructionsService.get(page, pageSize, search).then((response) => {
            const all = [];
            if (backupInstructions) {
                all.push(...backupInstructions);
            }
            if (response) {
                response.forEach(element => {
                    if (!all.find((x) => x.key == element.key)) {
                        all.push(element);
                    }
                });
            }
            setBackupInstructions(all);
            setCurrentPage(page);
            setShowMore(response.length == pageSize);
        });
    }

    const deleteBackupInstruction = (e, id) => {
        if (!window.confirm("Вы уверены, что хотите удалить ресурс? Бэкапы перестанут создаваться.")) return;
        BackupInstructionsService.del(id).then(
            (response) => {
                refresh();
            },
            (error) => {
                setMessage(Helper.errorToString(error));
            }
        );
    }

    const activateBackupInstruction = (e, key) => {
        BackupInstructionsService.on(key).then(
            (response) => {
                refresh();
            },
            (error) => {
                setMessage(Helper.errorToString(error));
            }
        );
    }

    const deactivateBackupInstruction = (e, key) => {
        BackupInstructionsService.off(key).then(
            (response) => {
                refresh();
            },
            (error) => {
                setMessage(Helper.errorToString(error));
            }
        );
    }

    const refresh = () => {
        getBackupInstructions();
    }

    const cloneBI = (bi) => {
        setCloneData(bi);
    }

    const copyKey = (key) => {
        navigator.clipboard.writeText(key);
        setJustCopyBackupInstructionKey(key);
        setTimeout(() => { setJustCopyBackupInstructionKey(null); }, 3000);
    }

    useEffect(() => {
        refresh();
    }, []);

    useEffect(() => {
        if (isFirstSearchRender.current) {
            isFirstSearchRender.current = false;
            return;
        }
        setCurrentPage(1);
        const search = (searchQuery || '').trim() || null;
        BackupInstructionsService.get(1, pageSize, search).then(
            (response) => {
                setBackupInstructions(Array.isArray(response) ? response : []);
                setShowMore(response.length == pageSize);
            },
            (error) => {
                setMessage(Helper.errorToString(error));
            }
        );
    }, [searchQuery]);


    const list = Array.isArray(backupInstructions) ? backupInstructions : [];

    const selectedInstruction = backupInstructionKey
        ? list.find((item) => item.key === backupInstructionKey)
        : null;

    const storageBadge = (count, label, titles) => (
        <span className="badge bi-badge" title={titles || ''}>
            {label}: {count ?? 0}
        </span>
    );

    const rows = list.map((backupInstruction) => {
        const databases = backupInstruction.databases || [];
        const directories = backupInstruction.directories || [];
        const mediaStorages = backupInstruction.media_storages || [];

        const databaseNames = databases.map((db) => db.database).filter(Boolean).join(', ');
        const directoryNames = directories.map((dir) => dir.path).filter(Boolean).join(', ');
        const mediaNames = mediaStorages.map((media) => media.path).filter(Boolean).join(', ');

        const isCopied = justCopyBackupInstructionKey === backupInstruction.key;

        return <tr key={backupInstruction.key}>
            <td>
                <div className="bi-name">{backupInstruction.name}</div>
                {backupInstruction.description &&
                    <div className="bi-description">{backupInstruction.description}</div>}
                <div className="bi-tags">
                    <span className="badge bi-tag">{backupInstruction.use_tgz ? 'tar.gz' : '7z'}</span>
                    {backupInstruction.archive_prefix &&
                        <span className="badge bi-tag">{backupInstruction.archive_prefix}</span>}
                </div>
            </td>
            <td>
                <div className="d-flex align-items-center gap-2">
                    <code className="bi-key" title={backupInstruction.key}>{backupInstruction.key}</code>
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary bi-copy"
                        title={isCopied ? "Скопировано" : "Скопировать ключ"}
                        onClick={() => copyKey(backupInstruction.key)}
                    >
                        <em className={`fa ${isCopied ? 'fa-check' : 'fa-copy'}`}></em>
                    </button>
                </div>
            </td>
            <td>
                {backupInstruction.enabled
                    ? <span className="badge bi-status-on">Включён</span>
                    : <span className="badge bi-status-off">Выключен</span>}
            </td>
            <td className="text-nowrap">{backupInstruction.rotation}</td>
            <td>
                <div className="bi-badges">
                    {storageBadge(databases.length, 'БД', databaseNames)}
                    {storageBadge(directories.length, 'Каталоги', directoryNames)}
                    {storageBadge(mediaStorages.length, 'Media', mediaNames)}
                </div>
            </td>
            <td className="text-nowrap text-muted">
                {backupInstruction.creation_date
                    ? format(parseISO(backupInstruction.creation_date), "dd.MM.yyyy HH:mm")
                    : '—'}
            </td>
            <td>
                <div className="d-flex justify-content-end gap-1 bi-actions">
                    <button className="btn btn-sm btn-outline-secondary" title="Клонировать" onClick={() => cloneBI(backupInstruction)}><i className="fa fa-copy"></i></button>
                    <button className="btn btn-sm btn-outline-secondary" title="Редактировать" onClick={() => navigate("/backup_instructions/" + backupInstruction.key)}><i className="fa fa-pencil"></i></button>
                    {backupInstruction.enabled
                        ? <button className="btn btn-sm btn-outline-secondary" title="Отключить" onClick={(e) => deactivateBackupInstruction(e, backupInstruction.key)}><i className="fa fa-pause"></i></button>
                        : <button className="btn btn-sm btn-outline-secondary" title="Включить" onClick={(e) => activateBackupInstruction(e, backupInstruction.key)}><i className="fa fa-play"></i></button>}
                    <button className="btn btn-sm btn-outline-danger" title="Удалить" onClick={(e) => deleteBackupInstruction(e, backupInstruction.key)}><i className="fa fa-trash-o"></i></button>
                </div>
            </td>
        </tr>
    });

    return (<div>
        <MainTitle title="Бэкапы" />
        <div className="container-fluid backup-instructions">
            {message && (<div className="alert alert-danger mt-4">
                {message}
            </div>)}
            <div className="control-panel">
                <button type="button" className="btn btn-primary nowrap" onClick={() => setShowNewModal(true)}>
                    Новый ресурс
                </button>
                <div className="input-group search-panel">
                    <span className="input-group-text"><i className="fa fa-search"></i></span>
                    <input
                        type="text"
                        className="form-control"
                        placeholder="Поиск по ключу или названию..."
                        aria-label="Поиск по ключу или названию"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                </div>
            </div>

            <div className="table-responsive">
                <table className="table table-hover align-middle">
                    <thead>
                        <tr>
                            <th>Название</th>
                            <th>Ключ</th>
                            <th>Статус</th>
                            <th>Ротация</th>
                            <th>Объекты</th>
                            <th>Создан</th>
                            <th className="text-end">Действия</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0
                            ? <tr>
                                <td colSpan="7" className="text-center text-muted py-4">Пока нет ресурсов</td>
                            </tr>
                            : rows}
                    </tbody>
                </table>
            </div>

            {showMore &&
                <div className='text-center backup-instructions-load-more mt-4'><button onClick={loadMore} className='btn btn-success'>Показать еще</button></div>}

            {selectedInstruction &&
                <Modal onClose={(e) => { navigate("/backup_instructions"); }} show={true} title="Редактирование ресурса" id={"modal" + selectedInstruction.key}>
                    <BackupInstructionEditor data={selectedInstruction} onUpdated={refresh} />
                </Modal>}
            {showNewModal && <Modal show={true} title="Новый ресурс" id="newModal" onClose={() => setShowNewModal(false)}>
                <BackupInstructionEditor onUpdated={refresh} />
            </Modal>}
            {cloneData && <Modal show={true} title="Новый ресурс" id="newCloneModal" onClose={() => { setCloneData(null); }}>
                <BackupInstructionEditor onUpdated={refresh} cloneData={cloneData} />
            </Modal>}
            <Toasts message={message} setMessage={setMessage} />
        </div>
    </div>)
}

export default BackupInstructions
